import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../lib/supabase';

// ADT Express Brand Colors - Premium palette
const ADT_GREEN = [34, 197, 94];           // #22c55e - accent only
const ADT_GREEN_DARK = [22, 101, 52];      // #166534 - premium deep green
const ADT_GREEN_MID = [21, 128, 61];       // #15803d
const ADT_GREEN_LIGHT = [240, 253, 244];   // #f0fdf4
const ADT_GRAY_DARK = [17, 24, 39];        // #111827
const ADT_GRAY = [75, 85, 99];             // #4b5563
const ADT_GRAY_MED = [107, 114, 128];      // #6b7280
const ADT_GRAY_LIGHT = [248, 250, 252];    // #f8fafc
const ADT_BORDER = [226, 232, 240];        // #e2e8f0
const WHITE = [255, 255, 255];
const BADGE_PAID = [22, 101, 52];          // deep green
const BADGE_PARTIAL = [180, 83, 9];        // amber-700
const BADGE_UNPAID = [185, 28, 28];        // red-700

// Page dimensions (A4)
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 15;
const CONTENT_W = PAGE_W - MARGIN * 2;

function drawRect(doc, x, y, w, h, fillColor, strokeColor = null, radius = 0) {
  doc?.setFillColor(...fillColor);
  if (strokeColor) {
    doc?.setDrawColor(...strokeColor);
    doc?.setLineWidth(0.3);
  } else {
    doc?.setDrawColor(...fillColor);
  }
  if (radius > 0) {
    doc?.roundedRect(x, y, w, h, radius, radius, strokeColor ? 'FD' : 'F');
  } else {
    doc?.rect(x, y, w, h, strokeColor ? 'FD' : 'F');
  }
}

function setFont(doc, size, style = 'normal', color = ADT_GRAY_DARK) {
  doc?.setFontSize(size);
  doc?.setFont('helvetica', style);
  doc?.setTextColor(...color);
}

function drawLine(doc, x1, y1, x2, y2, color = ADT_BORDER, width = 0.3) {
  doc?.setDrawColor(...color);
  doc?.setLineWidth(width);
  doc?.line(x1, y1, x2, y2);
}

function safeText(val) {
  if (!val || val === 'A definir' || val === 'À définir' || val === 'undefined' || val === 'null') return '-';
  return String(val);
}

export const invoiceService = {
  async generateInvoicePDF(shipmentData, items) {
    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

    // ── BACKGROUND ──────────────────────────────────────────────────────────
    drawRect(doc, 0, 0, PAGE_W, PAGE_H, WHITE);

    // ── TOP ACCENT BAR ───────────────────────────────────────────────────────
    drawRect(doc, 0, 0, PAGE_W, 4, ADT_GREEN_DARK);

    let curY = 12;

    // ── HEADER SECTION ───────────────────────────────────────────────────────
    let logoLoaded = false;
    try {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.src = '/assets/images/banniere_adt-1763665656406.png';
      await new Promise((resolve) => {
        logoImg.onload = () => { logoLoaded = true; resolve(); };
        logoImg.onerror = () => resolve();
        setTimeout(resolve, 4000);
      });
      if (logoLoaded && logoImg?.naturalWidth > 0) {
        const logoW = 64;
        const logoH = (logoImg?.naturalHeight / logoImg?.naturalWidth) * logoW;
        const safeH = Math.min(logoH, 22);
        doc?.addImage(logoImg, 'PNG', MARGIN, curY, logoW, safeH);
      }
    } catch (e) {
      logoLoaded = false;
    }

    if (!logoLoaded) {
      setFont(doc, 20, 'bold', ADT_GREEN_DARK);
      doc?.text('ADT EXPRESS', MARGIN, curY + 12);
      setFont(doc, 8, 'normal', ADT_GRAY);
      doc?.text('Transport & Logistique Maritime', MARGIN, curY + 18);
    }

    // Right side: FACTURE title + details
    const rightX = PAGE_W - MARGIN;

    setFont(doc, 22, 'bold', ADT_GRAY_DARK);
    doc?.text('FACTURE', rightX, curY + 8, { align: 'right' });

    setFont(doc, 8.5, 'normal', ADT_GRAY);
    const invoiceRef = shipmentData?.invoiceReference || `FAC-${new Date()?.getFullYear()}-${String(Math.floor(Math.random() * 999999))?.padStart(6, '0')}`;
    const shipRef = shipmentData?.reference || '-';
    const invoiceDate = new Date()?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

    doc?.text(`Facture : ${invoiceRef}`, rightX, curY + 15, { align: 'right' });
    doc?.text(`Envoi : ${shipRef}`, rightX, curY + 20, { align: 'right' });
    doc?.text(`Date : ${invoiceDate}`, rightX, curY + 25, { align: 'right' });

    // Payment status badge - SaaS style
    const paymentStatus = shipmentData?.payment_status || 'non_paye';
    let badgeColor = BADGE_UNPAID;
    let badgeText = 'NON PAYE';
    if (paymentStatus === 'paye') {
      badgeColor = BADGE_PAID;
      badgeText = 'PAYE';
    } else if (paymentStatus === 'partiel') {
      badgeColor = BADGE_PARTIAL;
      badgeText = 'PARTIEL';
    } else if (paymentStatus === 'non_paye') {
      badgeColor = BADGE_UNPAID;
      badgeText = 'NON PAYE';
    }

    const badgeW = 30;
    const badgeH = 7.5;
    const badgeX = rightX - badgeW;
    const badgeY = curY + 28;
    drawRect(doc, badgeX, badgeY, badgeW, badgeH, badgeColor, null, 3);
    setFont(doc, 7, 'bold', WHITE);
    doc?.text(badgeText, badgeX + badgeW / 2, badgeY + 5.2, { align: 'center' });

    curY += 42;

    // ── DIVIDER ──────────────────────────────────────────────────────────────
    drawLine(doc, MARGIN, curY, PAGE_W - MARGIN, curY, ADT_BORDER, 0.5);
    curY += 7;

    // ── CLIENT CARDS ─────────────────────────────────────────────────────────
    const cardW = (CONTENT_W - 6) / 2;
    const cardH = 40;
    const cardY = curY;

    // Helper to draw a client card
    const drawClientCard = (x, label, name, address, cityCountry, phone) => {
      drawRect(doc, x, cardY, cardW, cardH, ADT_GRAY_LIGHT, ADT_BORDER, 3);
      // Green header strip
      drawRect(doc, x, cardY, cardW, 6, ADT_GREEN_DARK, null, 3);
      drawRect(doc, x, cardY + 3, cardW, 3, ADT_GREEN_DARK); // fill bottom of rounded corners

      setFont(doc, 7, 'bold', WHITE);
      doc?.text(label, x + 4, cardY + 4.5);

      setFont(doc, 9.5, 'bold', ADT_GRAY_DARK);
      doc?.text(safeText(name), x + 4, cardY + 13);

      setFont(doc, 8, 'normal', ADT_GRAY);
      let lineY = cardY + 19;
      const lineStep = 5.5;

      if (address && address !== '-') {
        doc?.text(safeText(address), x + 4, lineY);
        lineY += lineStep;
      }
      if (cityCountry && cityCountry !== '-') {
        doc?.text(safeText(cityCountry), x + 4, lineY);
        lineY += lineStep;
      }
      if (phone && phone !== '-') {
        // Use plain text prefix instead of emoji
        doc?.text('Tel : ' + safeText(phone), x + 4, lineY);
      }
    };

    const senderCityCountry = [shipmentData?.senderCity, shipmentData?.senderCountry]?.filter(v => v && v !== '-')?.join(', ') || '-';
    const receiverCityCountry = [shipmentData?.receiverCity, shipmentData?.receiverCountry]?.filter(v => v && v !== '-')?.join(', ') || '-';

    drawClientCard(MARGIN, 'EXPEDITEUR', shipmentData?.senderName, shipmentData?.senderAddress, senderCityCountry, shipmentData?.senderPhone);
    drawClientCard(MARGIN + cardW + 6, 'DESTINATAIRE', shipmentData?.receiverName, shipmentData?.receiverAddress, receiverCityCountry, shipmentData?.receiverPhone);

    curY = cardY + cardH + 9;

    // ── ARTICLES TABLE ────────────────────────────────────────────────────────
    setFont(doc, 10, 'bold', ADT_GRAY_DARK);
    doc?.text('DETAIL DES ARTICLES', MARGIN, curY);
    curY += 5;

    const tableData = (items || [])?.map(item => [
      safeText(item?.catalogItemName || 'Article'),
      String(item?.quantity || 0),
      `${(Number(item?.priceUnit) || 0)?.toFixed(2)} EUR`,
      `${(Number(item?.priceTotal) || 0)?.toFixed(2)} EUR`
    ]);

    autoTable(doc, {
      startY: curY,
      head: [['Article', 'Qte', 'Prix unitaire', 'Total']],
      body: tableData?.length > 0 ? tableData : [['Aucun article renseigne', '-', '-', '-']],
      theme: 'plain',
      headStyles: {
        fillColor: ADT_GREEN_DARK,
        textColor: WHITE,
        fontStyle: 'bold',
        fontSize: 9,
        cellPadding: { top: 4.5, bottom: 4.5, left: 5, right: 5 },
      },
      bodyStyles: {
        fontSize: 9,
        textColor: ADT_GRAY_DARK,
        cellPadding: { top: 4, bottom: 4, left: 5, right: 5 },
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251],
      },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 18, halign: 'center' },
        2: { cellWidth: 34, halign: 'right' },
        3: { cellWidth: 34, halign: 'right', fontStyle: 'bold' },
      },
      tableLineColor: ADT_BORDER,
      tableLineWidth: 0.3,
      margin: { left: MARGIN, right: MARGIN },
    });

    curY = doc?.lastAutoTable?.finalY + 10;

    // ── TOTALS + QR CODE SECTION ──────────────────────────────────────────────
    const totalArticles = (items || [])?.reduce((sum, item) => sum + (Number(item?.priceTotal) || 0), 0);
    const discountAmount = Number(shipmentData?.discount_amount) || 0;
    const totalFinal = Math.max(totalArticles - discountAmount, 0);
    let paidAmount = Number(shipmentData?.paid_amount) || 0;
    const remainingAmount = Math.max(totalFinal - paidAmount, 0);

    const totalsBoxW = 88;
    const totalsX = PAGE_W - MARGIN - totalsBoxW;
    const totalsStartY = curY;

    // QR Code (left side, aligned with totals)
    const qrX = MARGIN;
    const qrY = totalsStartY;
    const trackingUrl = `https://adtexpress.cloud/tracking/${shipmentData?.reference || ''}`;

    try {
      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(trackingUrl)}&color=166534&bgcolor=ffffff&margin=2`;
      const qrImg = new Image();
      qrImg.crossOrigin = 'anonymous';
      qrImg.src = qrApiUrl;
      await new Promise((resolve) => {
        qrImg.onload = resolve;
        qrImg.onerror = resolve;
        setTimeout(resolve, 5000);
      });
      if (qrImg?.complete && qrImg?.naturalWidth > 0) {
        const qrSize = 30;
        const boxPad = 4;
        drawRect(doc, qrX, qrY, qrSize + boxPad * 2, qrSize + boxPad * 2 + 7, WHITE, ADT_BORDER, 3);
        doc?.addImage(qrImg, 'PNG', qrX + boxPad, qrY + boxPad, qrSize, qrSize);
        setFont(doc, 6.5, 'normal', ADT_GRAY_MED);
        doc?.text('Scanner pour suivre', qrX + boxPad + qrSize / 2, qrY + boxPad + qrSize + 4, { align: 'center' });
        doc?.text("l'envoi", qrX + boxPad + qrSize / 2, qrY + boxPad + qrSize + 8, { align: 'center' });
      }
    } catch (e) {
      // QR optional
    }

    // Totals rows
    let totalsY = totalsStartY;
    const rowH = 7.5;

    const drawTotalRow = (label, value, labelColor = ADT_GRAY, valueColor = ADT_GRAY_DARK, bold = false) => {
      setFont(doc, 8.5, 'normal', labelColor);
      doc?.text(label, totalsX + 4, totalsY + 5.5);
      setFont(doc, 8.5, bold ? 'bold' : 'normal', valueColor);
      doc?.text(value, totalsX + totalsBoxW - 4, totalsY + 5.5, { align: 'right' });
      totalsY += rowH;
    };

    drawTotalRow('Sous-total', `${totalArticles?.toFixed(2)} EUR`);

    if (discountAmount > 0) {
      drawTotalRow('Remise', `-${discountAmount?.toFixed(2)} EUR`, ADT_GRAY, [185, 28, 28]);
    }

    // Divider before total TTC
    drawLine(doc, totalsX, totalsY, totalsX + totalsBoxW, totalsY, ADT_BORDER, 0.4);
    totalsY += 2;

    // TOTAL TTC block - premium deep green, not aggressive
    drawRect(doc, totalsX, totalsY, totalsBoxW, 12, ADT_GREEN_DARK, null, 3);
    setFont(doc, 10, 'bold', WHITE);
    doc?.text('TOTAL TTC', totalsX + 5, totalsY + 8.5);
    doc?.text(`${totalFinal?.toFixed(2)} EUR`, totalsX + totalsBoxW - 5, totalsY + 8.5, { align: 'right' });
    totalsY += 15;

    // Payment details
    drawTotalRow('Montant paye', `${paidAmount?.toFixed(2)} EUR`, ADT_GRAY, [22, 101, 52], true);

    const remainColor = remainingAmount > 0 ? [185, 28, 28] : [22, 101, 52];
    drawTotalRow('Reste a payer', `${remainingAmount?.toFixed(2)} EUR`, ADT_GRAY, remainColor, true);

    // Payment method
    if (shipmentData?.payment_method) {
      const methodMap = {
        'espece': 'Espece',
        'cash': 'Espece',
        'carte': 'Carte bancaire',
        'virement': 'Virement bancaire',
        'cheque': 'Cheque',
        'mobile': 'Paiement mobile',
      };
      const methodLabel = methodMap?.[shipmentData?.payment_method?.toLowerCase()] || safeText(shipmentData?.payment_method);
      drawTotalRow('Methode de paiement', methodLabel, ADT_GRAY, ADT_GRAY_DARK);
    }

    // Shipment status
    if (shipmentData?.status) {
      const statusMap = {
        'en_attente': 'En attente',
        'depose': 'Depose',
        'en_preparation': 'En preparation',
        'en_transit': 'En transit',
        'arrive_abidjan': 'Arrive Abidjan',
        'en_livraison': 'En livraison',
        'livre': 'Livre',
        'annule': 'Annule',
      };
      const statusLabel = statusMap?.[shipmentData?.status] || safeText(shipmentData?.status);
      drawTotalRow('Statut expedition', statusLabel, ADT_GRAY, ADT_GRAY_DARK);
    }

    curY = Math.max(totalsY + 6, totalsStartY + 60);

    // ── FOOTER ───────────────────────────────────────────────────────────────
    // Calculate footer start - ensure it fits on page
    const footerStartY = PAGE_H - 62;

    drawLine(doc, MARGIN, footerStartY, PAGE_W - MARGIN, footerStartY, ADT_BORDER, 0.5);

    // Thank you message
    setFont(doc, 9.5, 'bold', ADT_GRAY_DARK);
    doc?.text('Merci pour votre confiance.', PAGE_W / 2, footerStartY + 7, { align: 'center' });
    setFont(doc, 8.5, 'normal', ADT_GRAY);
    doc?.text("L'equipe ADT Express.", PAGE_W / 2, footerStartY + 13, { align: 'center' });

    drawLine(doc, MARGIN, footerStartY + 17, PAGE_W - MARGIN, footerStartY + 17, ADT_BORDER, 0.2);

    // Legal block
    const legalY = footerStartY + 22;
    setFont(doc, 7, 'bold', ADT_GRAY_MED);
    doc?.text('ADT EXPRESS est une marque de TRANSIMPEX INTERNATIONAL SERVICES', PAGE_W / 2, legalY, { align: 'center' });

    setFont(doc, 6.5, 'normal', ADT_GRAY_MED);
    doc?.text('Societe par actions simplifiee unipersonnelle (SASU) — RCS Bobigny : 990 952 806', PAGE_W / 2, legalY + 5, { align: 'center' });
    doc?.text('Siege social : 34 Rue Hemet, 93300 Aubervilliers, France', PAGE_W / 2, legalY + 10, { align: 'center' });

    drawLine(doc, MARGIN, legalY + 14, PAGE_W - MARGIN, legalY + 14, ADT_BORDER, 0.2);

    // Contact info - plain text, no Unicode icons
    const contactY = legalY + 19;
    setFont(doc, 7, 'normal', ADT_GRAY_MED);

    const col1X = MARGIN + 5;
    const col2X = PAGE_W / 2;
    const col3X = PAGE_W - MARGIN - 5;

    doc?.text('Email : contact@adtexpress.cloud', col1X, contactY);
    doc?.text('Web : www.adtexpress.cloud', col2X, contactY, { align: 'center' });
    doc?.text('WhatsApp : +33 6 35 52 82 31', col3X, contactY, { align: 'right' });

    drawLine(doc, MARGIN, contactY + 5, PAGE_W - MARGIN, contactY + 5, ADT_BORDER, 0.2);

    // Auto-generated notice
    setFont(doc, 6, 'normal', [156, 163, 175]);
    doc?.text('Document genere automatiquement par ADT Express', PAGE_W / 2, contactY + 10, { align: 'center' });

    // Bottom green accent bar
    drawRect(doc, 0, PAGE_H - 5, PAGE_W, 5, ADT_GREEN_DARK);

    const pdfBlob = doc?.output('blob');
    return pdfBlob;
  },

  async uploadInvoice(shipmentReference, pdfBlob) {
    const fileName = `invoice-${shipmentReference}-${Date.now()}.pdf`;
    const filePath = `${shipmentReference}/${fileName}`;

    const { data, error: uploadError } = await supabase?.storage?.from('invoices')?.upload(filePath, pdfBlob, {
        contentType: 'application/pdf',
        upsert: false
      });

    if (uploadError) throw uploadError;

    const { data: urlData, error: urlError } = await supabase?.storage?.from('invoices')?.createSignedUrl(data?.path, 31536000);

    if (urlError) throw urlError;

    return {
      filePath: data?.path,
      signedUrl: urlData?.signedUrl
    };
  },

  async updateShipmentInvoiceUrl(shipmentId, invoiceUrl) {
    const { error } = await supabase?.from('shipments')?.update({ invoice_url: invoiceUrl })?.eq('id', shipmentId);

    if (error) throw error;
  },

  async createInvoiceFileRecord(shipmentId, invoiceUrl, filePath) {
    const fileName = filePath?.split('/')?.pop() || 'invoice.pdf';
    
    const { error } = await supabase?.from('shipment_files')?.insert({
        shipment_id: shipmentId,
        file_type: 'invoice',
        file_url: invoiceUrl,
        file_name: fileName
      });

    if (error) throw error;
  },

  async getInvoiceUrl(shipmentReference) {
    const { data, error } = await supabase?.from('shipments')?.select('invoice_url')?.eq('reference', shipmentReference)?.maybeSingle();

    if (error) throw error;

    if (!data?.invoice_url) {
      return null;
    }

    return data?.invoice_url;
  },

  async regenerateInvoice(shipmentId) {
    try {
      console.log('Debut regeneration facture pour shipmentId:', shipmentId);

      const { data: shipment, error: shipmentError } = await supabase?.from('shipments')?.select('*')?.eq('id', shipmentId)?.single();

      if (shipmentError) {
        throw new Error(`Erreur chargement envoi: ${shipmentError.message}`);
      }
      
      if (!shipment) {
        throw new Error('Envoi introuvable');
      }

      const { data: items, error: itemsError } = await supabase
        ?.from('shipment_items')
        ?.select(`*, catalog_items (name)`)
        ?.eq('shipment_id', shipmentId);

      if (itemsError) {
        throw new Error(`Erreur chargement articles: ${itemsError.message}`);
      }

      const totalArticles = (items || [])?.reduce((sum, item) => sum + (item?.price_total || 0), 0);
      const discountAmount = Number(shipment?.discount_amount) || 0;
      const totalFinal = Math.max(totalArticles - discountAmount, 0);

      const shipmentData = {
        reference: shipment?.reference,
        invoiceReference: shipment?.invoice_reference || null,
        senderName: shipment?.sender_name,
        senderAddress: shipment?.sender_address,
        senderCity: shipment?.sender_city,
        senderCountry: shipment?.sender_country,
        senderPhone: shipment?.sender_phone,
        receiverName: shipment?.receiver_name,
        receiverAddress: shipment?.receiver_address,
        receiverCity: shipment?.receiver_city,
        receiverCountry: shipment?.receiver_country,
        receiverPhone: shipment?.receiver_phone,
        discount_amount: discountAmount,
        totalArticles,
        totalFinal,
        paid_amount: shipment?.paid_amount || 0,
        payment_status: shipment?.payment_status || 'non_paye',
        payment_method: shipment?.payment_method,
        payment_notes: shipment?.payment_notes,
        status: shipment?.status,
      };

      const mappedItems = (items || [])?.map(item => ({
        catalogItemName: item?.catalog_items?.name || 'Article sans nom',
        quantity: item?.quantity || 0,
        priceUnit: item?.price_unit || 0,
        priceTotal: item?.price_total || 0
      }));

      const pdfBlob = await this.generateInvoicePDF(shipmentData, mappedItems);
      const { filePath, signedUrl } = await this.uploadInvoice(shipment?.reference, pdfBlob);
      await this.updateShipmentInvoiceUrl(shipmentId, signedUrl);
      await this.createInvoiceFileRecord(shipmentId, signedUrl, filePath);

      return {
        success: true,
        invoiceUrl: signedUrl,
        message: 'Facture regeneree avec succes'
      };
    } catch (error) {
      console.error('Erreur lors de la regeneration de la facture:', error);
      throw new Error(`Impossible de regenerer la facture: ${error.message}`);
    }
  },

  // ─── PROMO ORDER INVOICE METHODS ──────────────────────────────────────────

  /**
   * Generate invoice reference for promo orders
   */
  generatePromoInvoiceRef(orderId, type = 'deposit') {
    const date = new Date();
    const year = date?.getFullYear();
    const month = String(date?.getMonth() + 1)?.padStart(2, '0');
    const shortId = orderId?.slice(-6)?.toUpperCase();
    const prefix = type === 'deposit' ? 'ACOMPTE' : type === 'balance' ? 'SOLDE' : 'FINAL';
    return `ADT-${prefix}-${year}${month}-${shortId}`;
  },

  /**
   * Generate official ADT Express PDF for promo order invoice
   * Uses the same template as shipment invoices
   */
  async generatePromoInvoicePDF(order, invoiceData) {
    const {
      invoiceReference,
      invoiceType = 'deposit',
      qty,
      unitPromoPrice,
      unitDepositAmount,
      unitBalanceAmount,
      totalPromoAmount,
      totalDepositAmount,
      totalBalanceAmount,
      paidAmount,
      remainingAmount,
      prestataireName,
      prestataireAmount,
      adtAmount,
      encaissementType,
    } = invoiceData;

    // Build items for the PDF
    const items = [];
    if (invoiceType === 'deposit') {
      items?.push({
        catalogItemName: `Acompte — Promo Ete 2026 — Barrique noire 270 L + transport Abidjan`,
        quantity: qty,
        priceUnit: unitDepositAmount,
        priceTotal: totalDepositAmount,
      });
    } else if (invoiceType === 'balance') {
      items?.push({
        catalogItemName: `Solde — Promo Ete 2026 — Barrique noire 270 L + transport Abidjan`,
        quantity: qty,
        priceUnit: unitBalanceAmount,
        priceTotal: totalBalanceAmount,
      });
    } else {
      // final invoice - show all
      items?.push({
        catalogItemName: `Barrique noire 270 L — Promo Ete 2026 (tarif tout compris)`,
        quantity: qty,
        priceUnit: unitPromoPrice,
        priceTotal: totalPromoAmount,
      });
    }

    const paymentStatus = remainingAmount <= 0 ? 'paye' : paidAmount > 0 ? 'partiel' : 'non_paye';

    const shipmentData = {
      reference: order?.deposit_invoice_ref || invoiceReference,
      invoiceReference,
      senderName: order?.client_name,
      senderAddress: order?.delivery_address || '',
      senderCity: order?.delivery_city || '',
      senderCountry: 'France',
      senderPhone: order?.client_phone || '',
      receiverName: 'ADT Express',
      receiverAddress: '34 Rue Hemet',
      receiverCity: 'Aubervilliers',
      receiverCountry: 'France',
      receiverPhone: '+33 6 35 52 82 31',
      discount_amount: 0,
      totalArticles: invoiceType === 'deposit' ? totalDepositAmount : invoiceType === 'balance' ? totalBalanceAmount : totalPromoAmount,
      totalFinal: invoiceType === 'deposit' ? totalDepositAmount : invoiceType === 'balance' ? totalBalanceAmount : totalPromoAmount,
      paid_amount: paidAmount,
      payment_status: paymentStatus,
      payment_method: encaissementType === 'prestataire' ? `Prestataire: ${prestataireName || ''}` : 'ADT',
      status: 'promo',
    };

    // Generate using the same PDF engine
    const pdfBlob = await this.generateInvoicePDF(shipmentData, items);

    // Add promo-specific summary page if deposit
    return pdfBlob;
  },

  /**
   * Create or get existing promo invoice in adt_invoices table
   * Returns existing invoice if already created (no duplicates)
   */
  async createPromoInvoice(order, invoiceType = 'deposit') {
    // Check if invoice already exists
    const existingField = invoiceType === 'deposit' ? 'deposit_invoice_id' : 'final_invoice_id';
    const existingId = order?.[existingField];

    if (existingId) {
      const { data: existing } = await supabase
        ?.from('adt_invoices')
        ?.select('*')
        ?.eq('id', existingId)
        ?.maybeSingle();
      if (existing) return { invoice: existing, isNew: false };
    }

    // Also check by promo_order_id + invoice_type
    const { data: existingByOrder } = await supabase
      ?.from('adt_invoices')
      ?.select('*')
      ?.eq('promo_order_id', order?.id)
      ?.eq('invoice_type', invoiceType)
      ?.maybeSingle();

    if (existingByOrder) {
      // Link it back to the order
      const updateField = invoiceType === 'deposit' ? { deposit_invoice_id: existingByOrder?.id } : { final_invoice_id: existingByOrder?.id };
      await supabase?.from('promo_summer_orders')?.update(updateField)?.eq('id', order?.id);
      return { invoice: existingByOrder, isNew: false };
    }

    // Create new invoice
    const qty = order?.barrel_quantity || 1;
    const unitPromoPrice = Number(order?.unit_promo_price) || 160;
    const unitDepositAmount = Number(order?.unit_deposit_amount) || 50;
    const unitBalanceAmount = Number(order?.unit_balance_amount) || 110;
    const totalPromoAmount = qty * unitPromoPrice;
    const totalDepositAmount = qty * unitDepositAmount;
    const totalBalanceAmount = qty * unitBalanceAmount;

    const invoiceRef = this.generatePromoInvoiceRef(order?.id, invoiceType);

    let totalAmount, paidAmount;
    if (invoiceType === 'deposit') {
      totalAmount = totalPromoAmount;
      paidAmount = totalDepositAmount;
    } else if (invoiceType === 'balance') {
      totalAmount = totalPromoAmount;
      paidAmount = totalPromoAmount; // fully paid when balance invoice created
    } else {
      totalAmount = totalPromoAmount;
      paidAmount = order?.deposit_paid ? totalDepositAmount : 0;
    }

    const encaissementType = order?.encaissement_type || 'prestataire';
    const providerCost = Number(order?.unit_provider_cost) || 30;
    const adtRevenue = Number(order?.unit_adt_revenue) || 20;

    const invoicePayload = {
      source_type: 'promo_order',
      source_id: order?.id,
      invoice_type: invoiceType,
      promo_order_id: order?.id,
      invoice_reference: invoiceRef,
      total_amount: totalAmount,
      paid_amount: paidAmount,
      invoice_status: paidAmount >= totalAmount ? 'paid' : paidAmount > 0 ? 'partially_paid' : 'issued',
      encaissement_type: encaissementType,
      prestataire_name: order?.provider_name || null,
      prestataire_amount: encaissementType === 'prestataire' ? qty * providerCost : null,
      adt_amount: encaissementType === 'prestataire' ? qty * adtRevenue : null,
      payment_metadata: {
        barrel_quantity: qty,
        unit_promo_price: unitPromoPrice,
        unit_deposit_amount: unitDepositAmount,
        unit_balance_amount: unitBalanceAmount,
        total_promo_amount: totalPromoAmount,
        total_deposit_amount: totalDepositAmount,
        total_balance_amount: totalBalanceAmount,
        client_name: order?.client_name,
        client_phone: order?.client_phone,
        client_email: order?.client_email,
        delivery_address: order?.delivery_address,
        delivery_city: order?.delivery_city,
        delivery_postal_code: order?.delivery_postal_code,
      },
    };

    const { data: newInvoice, error } = await supabase
      ?.from('adt_invoices')
      ?.insert(invoicePayload)
      ?.select()
      ?.single();

    if (error) throw new Error(`Erreur creation facture: ${error.message}`);

    // Link invoice to promo order
    const updateField = invoiceType === 'deposit'
      ? { deposit_invoice_id: newInvoice?.id, deposit_invoice_ref: invoiceRef }
      : { final_invoice_id: newInvoice?.id, final_invoice_ref: invoiceRef };

    await supabase?.from('promo_summer_orders')?.update(updateField)?.eq('id', order?.id);

    return { invoice: newInvoice, isNew: true };
  },

  /**
   * Generate and upload PDF for a promo invoice, then update invoice_url
   */
  async generateAndUploadPromoInvoicePDF(order, invoice) {
    const invoiceType = invoice?.invoice_type || 'deposit';
    const qty = order?.barrel_quantity || 1;
    const unitPromoPrice = Number(order?.unit_promo_price) || 160;
    const unitDepositAmount = Number(order?.unit_deposit_amount) || 50;
    const unitBalanceAmount = Number(order?.unit_balance_amount) || 110;

    const invoiceData = {
      invoiceReference: invoice?.invoice_reference,
      invoiceType,
      qty,
      unitPromoPrice,
      unitDepositAmount,
      unitBalanceAmount,
      totalPromoAmount: qty * unitPromoPrice,
      totalDepositAmount: qty * unitDepositAmount,
      totalBalanceAmount: qty * unitBalanceAmount,
      paidAmount: Number(invoice?.paid_amount) || 0,
      remainingAmount: Number(invoice?.remaining_amount) || 0,
      prestataireName: invoice?.prestataire_name,
      prestataireAmount: Number(invoice?.prestataire_amount) || 0,
      adtAmount: Number(invoice?.adt_amount) || 0,
      encaissementType: invoice?.encaissement_type || 'prestataire',
    };

    const pdfBlob = await this.generatePromoInvoicePDF(order, invoiceData);
    const fileRef = `promo-${order?.id?.slice(-8)}`;
    const { filePath, signedUrl } = await this.uploadInvoice(fileRef, pdfBlob);

    // Update invoice_url in adt_invoices
    await supabase?.from('adt_invoices')?.update({ invoice_url: signedUrl })?.eq('id', invoice?.id);

    return signedUrl;
  },

  /**
   * Get all invoices for a promo order
   */
  async getPromoOrderInvoices(promoOrderId) {
    const { data, error } = await supabase
      ?.from('adt_invoices')
      ?.select('*')
      ?.eq('promo_order_id', promoOrderId)
      ?.order('created_at', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  /**
   * Add payment to an invoice (deposit or balance)
   */
  async addPaymentToInvoice(invoiceId, paymentAmount, notes = '') {
    const { data: invoice, error: fetchError } = await supabase
      ?.from('adt_invoices')
      ?.select('*')
      ?.eq('id', invoiceId)
      ?.single();
    if (fetchError) throw fetchError;

    const newPaidAmount = Number(invoice?.paid_amount) + Number(paymentAmount);
    const newStatus = newPaidAmount >= Number(invoice?.total_amount) ? 'paid' : 'partially_paid';

    const { data, error } = await supabase
      ?.from('adt_invoices')
      ?.update({
        paid_amount: newPaidAmount,
        invoice_status: newStatus,
        paid_at: newStatus === 'paid' ? new Date()?.toISOString() : null,
        payment_metadata: {
          ...(invoice?.payment_metadata || {}),
          last_payment: { amount: paymentAmount, date: new Date()?.toISOString(), notes },
        },
      })
      ?.eq('id', invoiceId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Link an existing promo invoice to a shipment (when converting promo → envoi)
   */
  async linkInvoiceToShipment(invoiceId, shipmentId) {
    const { data, error } = await supabase
      ?.from('adt_invoices')
      ?.update({ shipment_id: shipmentId })
      ?.eq('id', invoiceId)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  /**
   * Get all invoices for a client (from both shipments and promo orders)
   */
  async getClientInvoices(clientId) {
    // Get promo order invoices
    const { data: promoOrders } = await supabase
      ?.from('promo_summer_orders')
      ?.select('id')
      ?.eq('client_id', clientId);

    const promoOrderIds = (promoOrders || [])?.map(o => o?.id);

    let promoInvoices = [];
    if (promoOrderIds?.length > 0) {
      const { data } = await supabase
        ?.from('adt_invoices')
        ?.select('*')
        ?.in('promo_order_id', promoOrderIds)
        ?.order('created_at', { ascending: false });
      promoInvoices = data || [];
    }

    // Get shipment invoices
    const { data: shipments } = await supabase
      ?.from('shipments')
      ?.select('id, reference, invoice_url, invoice_reference')
      ?.eq('client_id', clientId);

    return {
      promoInvoices,
      shipmentInvoices: shipments || [],
    };
  },
};